import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import SectionStatus from './SectionStatus';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import {
  allFeedbackSections,
  feedbackSectionIdToString,
  labelToSectionId,
} from 'npo/pages/NPOFeedback/utils';
import { IncompleteReportObject } from 'npo/pages/ReportBuilder/Components/Review/utils';
import {
  IconDefinition,
  faFileInvoiceDollar,
  faTypewriter,
  faUsers,
  faBook,
  faNotebook,
  faCalendarDays,
  faMapPin,
  faMemoCircleInfo,
  faWavePulse,
  faChartLineUp,
  faMagnifyingGlassArrowRight,
} from '@fortawesome/pro-light-svg-icons';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import {
  FINAL_REPORT_SECTIONS,
  NavOption,
  SECTIONS,
} from 'npo/pages/ReportBuilder/constants';
import { ReportingTaskState } from './sectionText';
import { scrollToTop } from '../../utils/pageUtils';
import { useGraphQLRequest } from '../../auth/hooks/useApiRequest';
import { ReportAPIResponse } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import { getReportIndicators } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/api';
import { Report } from './utils';
import { useClient } from '../../apollo';
import { getLastViewedSections } from './sectionLocalStorage';
import { useIndicator } from '../../providers/IndicatorProvider';
import { isBbUserView } from '../../utils/partnerUser';

const isBbPartner = (): boolean => isBbUserView();

const getOptions = (
  isReportPassed: boolean,
  options: NavOption[],
): NavOption[] => {
  if (!isReportPassed) return options;
  return FINAL_REPORT_SECTIONS()[0].options;
};

const getButtonText = (
  state: ReportingTaskState,
  lastViewedSection: boolean,
  sectionId: FeedbackSectionId,
): string => {
  const sectionName = feedbackSectionIdToString(sectionId);
  return state === 'COMPLETED'
    ? 'Review/Edit'
    : lastViewedSection
    ? 'Continue where you left off'
    : state === 'STARTED'
    ? 'Pick up where you left off'
    : sectionName !== 'Miscellaneous'
    ? `Begin ${sectionName}`
    : 'Let’s go!';
};

export const getSectionIcon = (id: FeedbackSectionId): IconDefinition => {
  switch (id) {
    case 'budget':
      return faFileInvoiceDollar;
    case 'dates':
      return faCalendarDays;
    case 'description':
      return faNotebook;
    case 'activities':
      return faWavePulse;
    case 'impact_summary':
      return faMemoCircleInfo;
    case 'indicator_details':
      return faChartLineUp;
    case 'location':
      return faMapPin;
    case 'narrative':
      return faTypewriter;
    case 'beneficiaries':
      return faUsers;
    case 'finalize_report':
      return faMagnifyingGlassArrowRight;

    case 'allocation':
    default:
      return faBook;
  }
};

const StyledDiv = styled.div`
  display: flex;
  max-width: 492px;
  flex-direction: column;
  gap: ${SPACERS.medium};
  margin-left: auto;
  margin-right: auto;
`;

export interface SectionsProps {
  report: Report;
  isLoading: boolean;
  isNewReport: boolean;
  isReportPassed: boolean;
  isReportReadyToSubmit: boolean;
  incomplete: IncompleteReportObject;
  handleSectionChange: (sectionId: FeedbackSectionId) => unknown;
}

const getSectionState = (
  id: FeedbackSectionId,
  incomplete: IncompleteReportObject,
  isNewReport: boolean,
  impactIndicatorResponse: ReportAPIResponse | undefined,
): ReportingTaskState => {
  if (isNewReport) return 'NEW_REPORT';
  const incompleteAnswers = incomplete[id]?.completedAnswers.length === 0;
  if (
    incompleteAnswers &&
    id === 'indicator_details' &&
    impactIndicatorResponse
  ) {
    return impactIndicatorResponse.npoIndicators.length > 0
      ? 'STARTED'
      : 'NOT_STARTED';
  }
  if (incomplete[id]) {
    return incompleteAnswers ? 'NOT_STARTED' : 'STARTED';
  }
  return 'COMPLETED';
};

const Sections: React.FC<SectionsProps> = ({
  report,
  isLoading,
  incomplete,
  isNewReport,
  isReportPassed,
  isReportReadyToSubmit,
  handleSectionChange,
}) => {
  const client = useClient();
  const { isLoading: indicatorLoading } = useIndicator();
  const lastViewedSection = getLastViewedSections()[report.dbId];
  const [reportRequestState, makeReportRequest] = useGraphQLRequest<{
    allReports: [ReportAPIResponse];
  }>();
  useEffect(() => {
    client &&
      report?.dbId &&
      makeReportRequest(getReportIndicators(client!, report.dbId));
    scrollToTop();
  }, []);

  const sections = useMemo(() => {
    const options = getOptions(isReportPassed, SECTIONS[0].options);
    const result: {
      [x in FeedbackSectionId]?: {
        label: string;
        sectionId: FeedbackSectionId;
        sectionIndex: number;
      };
    } = {};
    for (const option of options) {
      const sectionId = labelToSectionId(option.text);
      if (!sectionId) continue;
      result[sectionId] = {
        sectionId,
        label: option.text,
        sectionIndex: option.id,
      };
    }
    return result;
  }, [lastViewedSection]);

  return (
    <StyledDiv>
      {!isBbPartner() && (
        <TrueImpactText variant="heading-page" align="center">
          {isNewReport ? (
            <>
              Welcome to your report!
              <br />
              Let’s get started.
            </>
          ) : (
            <>
              Your report is coming along nicely.
              <br />
              Let’s keep going!
            </>
          )}
        </TrueImpactText>
      )}
      {allFeedbackSections.map(section => {
        const sectionId = section.sectionId;
        if (!isReportPassed && sectionId === 'narrative') return null;
        const state = getSectionState(
          section.sectionId,
          incomplete,
          isNewReport,
          reportRequestState.data?.allReports[0],
        );
        const buttonText = getButtonText(
          state,
          lastViewedSection === sectionId,
          sectionId,
        );
        const sectionInfo = sections[sectionId];
        const loading =
          isLoading ||
          (sectionId === 'impact_summary' || sectionId === 'indicator_details'
            ? indicatorLoading
            : false);

        return (
          <SectionStatus
            isLoading={loading}
            isNewReport={isNewReport}
            isLastViewedSection={sectionId === lastViewedSection}
            sectionId={sectionId}
            sectionName={section.label}
            state={state}
            icon={getSectionIcon(sectionId)}
            button={
              sectionInfo
                ? {
                    text: buttonText,
                    onClick: () => handleSectionChange(sectionId),
                  }
                : undefined
            }
          />
        );
      })}
      {isReportReadyToSubmit && (
        <SectionStatus
          isLoading={isLoading}
          isNewReport={isNewReport}
          isLastViewedSection={false}
          sectionId="finalize_report"
          sectionName="Review and Submit"
          state="NOT_STARTED"
          icon={getSectionIcon('finalize_report')}
          button={{
            text: 'Review and Submit',
            onClick: () => handleSectionChange('finalize_report'),
          }}
        />
      )}
    </StyledDiv>
  );
};

export default Sections;
